import { Callout, LoaderScreen } from '@/components/Elements';
import { AnalyzeLayout } from '@/features/analyze/components/AnalyzeLayout';
import { FoamTreeSelectItem } from '@/features/analyze/components/FoamTreeSelectItem';
import { FoamTreeView } from '@/features/analyze/components/FoamTreeView';
import { PerPageSelect } from '@/features/analyze/components/PerPageSelect';
import { ResultNotFond } from '@/features/analyze/components/ResultNotFond';
import { useAnalyzeClustering } from '@/features/analyze/hooks/useAnalyzeClustering';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const TreeMap = () => {
  const { t } = useTranslation();
  const [isHintExpanded, setIsHintExpanded] = useState(true);
  const {
    formattedTreemapData,
    isLoading,
    selectedTileLabel,
    selectedArticle,
    isSelectedArticleLoading,
    onSelectTarget,
  } = useAnalyzeClustering();
  const analyzeHeaderRef = useRef<HTMLDivElement>(null);
  const headerHeight = analyzeHeaderRef.current?.getBoundingClientRect().bottom;

  if (!isLoading && !formattedTreemapData.length) {
    return (
      <AnalyzeLayout>
        <ResultNotFond />
      </AnalyzeLayout>
    );
  }

  return (
    <AnalyzeLayout analyzeHeaderRef={analyzeHeaderRef}>
      <div
        className="flex size-full gap-x-3 p-3"
        style={{ height: `calc(100vh - ${headerHeight}px)` }}
      >
        <div className="relative h-full w-2/3">
          {isLoading ? (
            <LoaderScreen contained />
          ) : (
            <>
              <FoamTreeView
                data={formattedTreemapData}
                onSelectTarget={onSelectTarget}
              />
              {isHintExpanded ? (
                <button
                  type="button"
                  className="absolute bottom-3 left-3 z-10 max-w-[calc(100%-1.5rem)] text-left"
                  onClick={() => setIsHintExpanded(false)}
                  aria-label={t('TreeMapの操作ヒントを閉じる')}
                >
                  <Callout variant="info" size="compact" inline>
                    {t(
                      '全体表示に戻るには、ESCキー押下または右ダブルクリックしてください。'
                    )}
                  </Callout>
                </button>
              ) : (
                <button
                  type="button"
                  className="absolute bottom-3 left-3 z-10 rounded-full border border-blue-200 bg-blue-50 p-2 text-blue-900 shadow-sm transition-colors hover:bg-blue-100"
                  onClick={() => setIsHintExpanded(true)}
                  aria-label={t('TreeMapの操作ヒントを表示する')}
                >
                  <InformationCircleIcon className="size-5" />
                </button>
              )}
            </>
          )}
        </div>
        <div className="flex h-full w-1/3 flex-col">
          <div className="flex items-center text-xs">
            {t('表示件数')}:
            <PerPageSelect />
          </div>

          <div className="flex-1 overflow-y-auto rounded-md bg-white p-3">
            {selectedTileLabel || selectedArticle ? (
              <div className="flex h-full min-h-0 flex-col gap-y-3">
                {selectedTileLabel && (
                  <p className="text-xs text-rw-sub">
                    {t('ブロック: {{label}}', { label: selectedTileLabel })}
                  </p>
                )}
                {isSelectedArticleLoading ? (
                  <div className="flex flex-1 items-center justify-center">
                    <LoaderScreen contained />
                  </div>
                ) : selectedArticle ? (
                  <FoamTreeSelectItem selectedArticle={selectedArticle} />
                ) : null}
              </div>
            ) : (
              <div className="flex h-full items-start">
                <Callout variant="info" size="compact">
                  {t('ダブルクリックでブロックを選択します。')}
                </Callout>
              </div>
            )}
          </div>
        </div>
      </div>
    </AnalyzeLayout>
  );
};
